(ns frontend.ux-overhaul.listing.generic.model
  (:require
   [clojure.string :as string]
   [common.opensearch.kanban-sort :as kanban-sort]
   [common.schema.listing-definition :as listing-definition]
   [frontend.ux-overhaul.listing.generic.legacy-views :as legacy-views]))

;; Generic listing runtime state model (re-frame db).
;; This namespace defines only structure + path helpers.
;; No event/view behavior here.

(def root-path [::state])

(defn listing-path
  [id]
  (into root-path [id]))

(defn section-path
  [id section-key]
  (into root-path [id section-key]))

(defn context-path [id] (section-path id :context))
(defn params-path [id] (section-path id :params))
(defn view-path [id] (section-path id :view))
(defn query-path [id] (section-path id :query))
(defn filters-path [id] (section-path id :filters))
(defn view-config-path [id] (section-path id :view-config))
(defn data-path [id] (section-path id :data))
(defn selection-path [id] (section-path id :selection))
(defn toolbar-path [id] (section-path id :toolbar))
(defn callbacks-path [id] (section-path id :callbacks))
(defn ui-path [id] (section-path id :ui))
(defn preview-path [id] (section-path id :preview))
(defn views-path [id] (section-path id :views))
(defn grid-column-filters-path [id] (section-path id :grid-column-filters))

(def kept-root-path
  "Where a listing keeps a part of its state when it is destroyed, for example when
   the user opens a record. This path is outside `root-path`, so it stays. The next
   start of the same listing gets the state back."
  [::kept])

(defn kept-path [id] (conj kept-root-path id))

;; Optional structural schema sketch for runtime state.
;; Keep this as documentation for event/sub/view implementation.
(def FieldPath
  [:or keyword? [:vector keyword?]])

(def ViewMode
  [:enum :grid :kanban :gallery :list])

(def view-mode-preference
  [:grid :kanban :gallery :list])

(defn preferred-view-mode
  [available-view-modes]
  (let [available-view-modes (set available-view-modes)]
    (or (some (fn [mode]
                (when (contains? available-view-modes mode)
                  mode))
              view-mode-preference)
        (first view-mode-preference))))

(def FilterValue
  [:or nil? string? number? boolean? keyword?])

(def ActiveFilter
  [:map
   [:field-path FieldPath]
   [:operator keyword?]
   [:values [:vector FilterValue]]])

(def ToolbarAction
  [:map
   [:id keyword?]
   [:label string?]
   [:enabled? boolean?]
   [:visible? boolean?]
   [:requires-selection? {:optional true} boolean?]
   ;; Visual emphasis for a :requires-selection? action rendered inline in
   ;; the selection action bar (see generic.view/selection-action-bar) -
   ;; :danger for destructive actions (bulk delete), absent/anything else
   ;; renders as the default neutral button style. Purely cosmetic - has no
   ;; effect on the "..." dropdown rendering.
   [:variant {:optional true} keyword?]
   ;; action callback should be dispatched via event id, not fn in db
   [:event {:optional true} [:vector any?]]])

(def ListingRuntime
  [:map
   [:id [:vector keyword? [:or int? [:= :no-pipeline]]]]

   [:params
    [:map
     [:entity-type keyword?]
     [:pipeline-id {:optional true} int?]
     [:po-default-field-paths [:vector FieldPath]]
     [:available-view-modes [:set ViewMode]]
     ;; Views that the listing offers without a saved entity. See
     ;; frontend.ux-overhaul.listing.pipeline-entity.default-views.
     [:default-views {:optional true} [:vector map?]]
     ;; The id of the listing for the whole entity type, for example
     ;; "oppo-listing" when :id has the pipeline. Only a listing for one
     ;; pipeline of many has it. A saved view can belong to it.
     [:type-listing-id {:optional true} string?]
     ;; The keys of the legacy grids whose saved views the listing shows. See
     ;; frontend.ux-overhaul.listing.generic.legacy-views.
     [:legacy-view-keys {:optional true} [:set keyword?]]
     [:filters {:optional true}
      [:map
       [:enabled? boolean?]
       [:editor-id {:optional true} string?]
       [:view-id {:optional true} any?]
       [:field-schemas {:optional true} any?]
       [:runtime-field-paths {:optional true} [:vector FieldPath]]
       [:load-facets-event {:optional true} [:maybe [:vector any?]]]]]]]

   [:context
    [:map
     ;; read from global session/account and flattened for this listing instance
     [:account-id {:optional true} int?]
     [:account-current-permissions {:optional true} any?]
     [:accessible-fields [:vector [:map
                                   [:field-path FieldPath]
                                   [:label {:optional true} string?]
                                   [:data-type {:optional true} keyword?]]]]
     [:accessible-field-paths [:set FieldPath]]]]

   [:view
    [:map
     [:mode ViewMode]
     [:previous-mode {:optional true} ViewMode]
     [:loading? boolean?]]]

   [:query
    [:map
     [:text string?]
     [:draft {:optional true} string?]
     [:debounce-ms pos-int?]]]

   [:filters
    [:map
     [:manage-open? boolean?]
     [:selected-field-path {:optional true} FieldPath]
     [:po-field-paths [:vector FieldPath]]
     [:pinned-field-paths [:vector FieldPath]]
     [:active [:vector ActiveFilter]]
     [:facet-options-by-field [:map-of FieldPath [:vector [:map
                                                           [:value FilterValue]
                                                           [:label {:optional true} string?]
                                                           [:count {:optional true} nat-int?]]]]]
     [:loading-facets-by-field [:map-of FieldPath boolean?]]
     [:field-search string?]
     [:facet-search string?]]]

   [:view-config
    [:map
     [:id {:optional true} int?]
     [:name {:optional true} string?]
     [:shared? boolean?]
     [:dirty? boolean?]
     [:columns {:optional true} [:vector FieldPath]]
     [:sort {:optional true} [:vector [:map [:field-path FieldPath] [:direction [:enum :asc :desc]]]]]
     [:group-by-field {:optional true} FieldPath]
     [:row-group-by-field {:optional true} FieldPath]
     [:row-group-sort-mode {:optional true} kanban-sort/sort-mode-enum]
     [:kanban-column-sort-mode {:optional true} kanban-sort/sort-mode-enum]
     [:kanban-card-sort-mode {:optional true} kanban-sort/sort-mode-enum]
     [:hide-empty-columns? {:optional true} boolean?]
     [:card {:optional true}
      [:map
       [:by-view-mode
        [:map-of ViewMode
         [:map
          [:show-labels? {:optional true} boolean?]
          [:body-fields {:optional true} [:vector FieldPath]]]]]]]
     [:raw {:optional true} any?]]]

   ;; The model of the column filters of the AG Grid, as it comes from
   ;; `api.getFilterModel()`, with the column ids as string keys. The grid is
   ;; destroyed when the user changes to another view mode, and the listing is
   ;; not, so the listing keeps the model for the next grid. It is not in
   ;; :view-config, as a saved view does not hold it.
   [:grid-column-filters [:map-of string? any?]]

   [:selection
    [:map
     [:selected-ids [:set any?]]
     [:last-selected-id {:optional true} any?]
     [:page-selection? boolean?]
     [:all-selection? boolean?]
     ;; Used only while :all-selection? is true. See
     ;; frontend.ux-overhaul.listing.generic.selection.
     [:excluded-ids {:optional true} [:set any?]]
     [:grid-id {:optional true} [:maybe string?]]
     [:resolved-ids {:optional true} [:maybe [:set any?]]]
     [:expected-count {:optional true} [:maybe int?]]]]

   [:toolbar
    [:map
     [:actions [:vector ToolbarAction]]
     [:visible? boolean?]]]

   [:callbacks
    [:map
     ;; keep callback references as re-frame events, not functions
     [:on-row-click-event {:optional true} [:vector any?]]
     [:on-item-click-event {:optional true} [:vector any?]]
     [:on-selection-changed-event {:optional true} [:vector any?]]
     [:on-view-mode-changed-event {:optional true} [:vector any?]]
     [:on-filters-changed-event {:optional true} [:vector any?]]
     [:on-view-config-saved-event {:optional true} [:vector any?]]
     [:on-view-config-shared-event {:optional true} [:vector any?]]]]

   [:data
    [:map
     [:status [:enum :idle :loading :loaded :error]]
     [:items [:vector any?]]
     [:total nat-int?]
     [:next-cursor {:optional true} any?]
     [:error {:optional true} any?]
     [:last-updated-at-ms {:optional true} nat-int?]]]

   [:ui
    [:map
     [:initialized? boolean?]
     ;; Optional global guard for exceptional transitions.
     [:busy? boolean?]
     ;; Fine-grained busy states by operation key, e.g.
     ;; {:filters/apply true :toolbar/bulk-delete false}
     [:busy-by-key [:map-of keyword? boolean?]]
     ;; Optional explicit control disabling map, keyed by control/action id.
     ;; Example:
     ;; {:view-mode/switch false
     ;;  :toolbar/bulk-archive true}
     [:disabled-by-key [:map-of keyword? boolean?]]
     ;; Optional human-readable reason for busy/disabled state.
     [:busy-reason {:optional true} string?]]]])

(defn default-runtime
  [{:keys [entity-type pipeline-id pipeline listing-id po-default-field-paths available-view-modes toolbar-actions callbacks filters
           persist-view-settings? default-views type-listing-id]}]
  (let [available-view-modes (or available-view-modes #{:grid :kanban :gallery :list})
        initial-mode (preferred-view-mode available-view-modes)
        filters (or filters {})
        filters-enabled? (boolean (:enabled? filters))
        persist-view-settings? (not (false? persist-view-settings?))
        po-default-field-paths (if (some? po-default-field-paths)
                                 (vec po-default-field-paths)
                                 (listing-definition/po-default-field-paths entity-type))]
    {:id         listing-id
     :params     {:entity-type            entity-type
                  :pipeline-id            pipeline-id
                  :pipeline               pipeline
                  :po-default-field-paths po-default-field-paths
                  :available-view-modes   (set available-view-modes)
                  :default-views          (vec default-views)
                  :type-listing-id        type-listing-id
                  :legacy-view-keys       (legacy-views/legacy-view-keys entity-type)
                  :persist-view-settings? persist-view-settings?
                  :filters                {:enabled? filters-enabled?
                                           :editor-id (:editor-id filters)
                                           :view-id (:view-id filters)
                                           :field-schemas (:field-schemas filters)
                                           :runtime-field-paths (vec (or (:runtime-field-paths filters) []))
                                           :load-facets-event (:load-facets-event filters)}}
     :context    {:account-id                   nil
                  :account-current-permissions  nil
                  :accessible-fields            []
                  :accessible-field-paths       #{}}
     :view       {:mode initial-mode
                  :previous-mode nil
                  :loading? false}
     :query      {:text ""
                  :draft ""
                  :debounce-ms 300}
     :filters    {:manage-open? false
                  :selected-field-path nil
                  :po-field-paths []
                  :pinned-field-paths []
                  :active []
                  :facet-options-by-field {}
                  :loading-facets-by-field {}
                  :field-search ""
                  :facet-search ""}
     :view-config {:id nil
                   :name nil
                   :shared? false
                   :dirty? false
                   :columns []
                   :sort []
                   :group-by-field nil
                   :row-group-by-field nil
                   :row-group-sort-mode kanban-sort/default-row-sort-mode
                   :kanban-column-sort-mode kanban-sort/default-column-sort-mode
                   :kanban-card-sort-mode kanban-sort/default-card-sort-mode
                   :hide-empty-columns? false
                   :card {:by-view-mode {}}
                   :raw nil}
     :grid-column-filters {}
     :selection  {:selected-ids #{}
                  :last-selected-id nil
                  :page-selection? false
                  :all-selection? false
                  :excluded-ids #{}}
     :toolbar    {:actions (vec (remove nil? toolbar-actions))
                  :visible? true}
     :callbacks  (or callbacks {})
     :data       {:status :idle
                  :items []
                  :total 0
                  :next-cursor nil
                  :error nil
                  :last-updated-at-ms nil}
     :ui         {:initialized? false
                  :busy? false
                  :busy-by-key {}
                  :disabled-by-key {}
                  :busy-reason nil}}))

(defn- by-lower-case-name
  [view]
  (string/lower-case (or (:named-view/name view) "")))

(defn- legacy-view
  "The entry of the views of the listing for a view of a legacy grid. It has the
   settings of the new listing, made without labels, so that the listing can show
   the view and tell when the user changes it. It has the decoded settings of the
   legacy view too, to make them again with labels when the user applies the
   view. The entry is read only, as a default view is: the old grid has the same
   entity. A view that has no readable settings gives nil."
  [{:keys [entity accessible-field-paths]}]
  (when-let [decoded (legacy-views/decode-settings (:named-view/settings entity))]
    {:db/id               (:db/id entity)
     :legacy-view?        true
     :default-view?       true
     :named-view/name     (:named-view/name entity)
     :legacy-settings     decoded
     :named-view/settings (legacy-views/legacy-view->settings
                           {:settings               decoded
                            :accessible-field-paths accessible-field-paths})}))

(defn saved-views
  "The views that a listing offers, as a vector: its default views first, in their
   own order, then the views of the legacy grids, and then the saved views of the
   user for this listing. The views of the legacy grids and the saved views are
   each sorted by name without regard to case. `entities` is the map of the session
   entities. A saved view is offered when it is not disabled, has the keyword of the
   listing or, when the listing has one, the keyword of the listing for the whole
   entity type, `type-listing-id`, and has the current schema version,
   `schema-version`. A view of a legacy grid is offered when it is not disabled, has
   one of `legacy-view-keys`, and has no schema version, as the legacy grids did not
   save one. `accessible-field-paths` are the fields that the user can read in the
   listing, for the legacy views."
  [{:keys [entities default-views listing-id type-listing-id schema-version
           legacy-view-keys accessible-field-paths]}]
  (let [view-kws (cond-> #{(keyword listing-id)}
                   type-listing-id (conj (keyword type-listing-id)))
        named-views (->> (vals entities)
                         (filter (fn [e]
                                   (and (= (:entity/type e) :entity.type/named-view)
                                        (not (:common/disabled? e))))))
        user-views (->> named-views
                        (filter (fn [e]
                                  (and (contains? view-kws (:named-view/view e))
                                       (= (:named-view/schema-version e) schema-version))))
                        (sort-by by-lower-case-name))
        legacy-views (->> named-views
                          (filter (fn [e]
                                    (and (contains? (set legacy-view-keys) (:named-view/view e))
                                         (nil? (:named-view/schema-version e)))))
                          (keep (fn [e]
                                  (legacy-view {:entity                 e
                                                :accessible-field-paths accessible-field-paths})))
                          (sort-by by-lower-case-name))]
    (-> (vec default-views)
        (into legacy-views)
        (into user-views))))

(defn- filters-signature
  "What a set of filters selects, without the labels and the ids that the filter
   bar adds. A label comes from the facets or from the language of the user, so
   the same choice can have two labels."
  [filters-by-field-path]
  (into {}
        (map (fn [[field-path {:keys [selections]}]]
               [field-path (set (map (juxt :operator :value :from :to) selections))]))
        filters-by-field-path))

(defn default-view-modified?
  "True when the filters that the listing uses now are not the filters of
   `default-view`. The view mode does not count, as a user can switch between
   the grid and the board and keep the same view."
  [default-view filters-by-field-path]
  (not= (filters-signature (get-in default-view [:named-view/settings :filters]))
        (filters-signature filters-by-field-path)))

(defn shown-active-view
  "The view that the panel shows as the active one. A default view is the active
   view only while its filters are in use. When a user changes a filter, the view
   is no longer that default view. A saved view stays active, as the panel marks
   its changes with the icon for a save. `dirty?` is true when the active view
   is changed."
  [{:keys [active-view dirty?]}]
  (when-not (and (:default-view? active-view)
                 dirty?)
    active-view))

(defn saved-view-key
  "The keyword for the `:named-view/view` of a new saved view. The scope
   `:entity-type` gives a view for all the pipelines of the type. It is the id of
   the listing for the type, `type-listing-id`. The scope `:pipeline`, a scope
   that is not known, and a listing that has no `type-listing-id` give the id of
   this listing."
  [{:keys [listing-id type-listing-id scope]}]
  (keyword (if (and (= :entity-type scope)
                    type-listing-id)
             type-listing-id
             listing-id)))

(defn all-pipelines-view?
  "True when `named-view` belongs to all the pipelines of the type, and not to
   one pipeline."
  [{:keys [named-view type-listing-id]}]
  (boolean
   (and type-listing-id
        (= (keyword type-listing-id)
           (:named-view/view named-view)))))

(defn grid-id
  "The id of the grid of a listing."
  [listing-id]
  (str listing-id "-grid"))

(defn kanban-id
  "The id of the Kanban of a listing."
  [listing-id]
  (str listing-id "-kanban"))

(defn grid-sort-modified?
  "True when the sort of the grid is not the sort that `saved-settings` has. A view
   saved before the grid sort was part of a view has no sort, and an open grid is
   needed to know the sort that is in use. In both cases the sort does not count.
   `live-sorted-by` is nil when the grid is not open."
  [{:keys [saved-settings live-sorted-by]}]
  (let [saved-sorted-by (get-in saved-settings [:grid :sorted-by])]
    (boolean
     (and (some? saved-sorted-by)
          (some? live-sorted-by)
          (not= saved-sorted-by live-sorted-by)))))

(defn card-body-fields
  "The fields that the body of a card shows. `chosen-fields` are the fields that
   the user chose: nil when the user did not choose, and an empty vector when the
   user removed all the fields. `accessible-fields` are the chosen fields that
   the user can still read. `default-fields` are the fields of the listing.

   A user who removed all the fields sees none. Before, the card then showed the
   default fields again, so the user could not have a card with no fields."
  [{:keys [chosen-fields accessible-fields default-fields]}]
  (cond
    (nil? chosen-fields) default-fields
    (empty? chosen-fields) []
    (seq accessible-fields) accessible-fields
    :else default-fields))
